import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CLIENT_KEYS } from "@constants/clientKeys";

import { apiClient } from "../../api/apiClient";

export type ExtractType = "snip" | "highlight" | "summary_highlight";

export type ExtractTargetField =
  | "summary_from_doc_sorter"
  | "summary_from_assessment_scribe";

export type MapExtractRequest = {
  case_number: string;
  module_ids: string[];
  text: string;
  source_file_name?: string;
  page_number?: number;
  category_slug?: string;
  extract_type?: ExtractType;
  target_field?: ExtractTargetField;
};

export type ExtractSourceResponse = {
  id: string;
  response_module_id: string;
  text: string;
  source_file_name: string | null;
  page_number: number | null;
  category_slug: string | null;
  extract_type: ExtractType;
  target_field: ExtractTargetField;
  order: number;
  created_at: string;
};

export const useMapExtractToModules = () => {
  return useMutation({
    mutationFn: async (
      data: MapExtractRequest
    ): Promise<ExtractSourceResponse[]> => {
      const { case_number, ...body } = data;
      const response = await apiClient.post({
        url: `/api/v1/report-generator/cases/${case_number}/module-extracts`,
        body,
        headers: { "Content-Type": "application/json" },
      });

      if (response.error) {
        throw new Error(
          `Failed to map extract to modules: ${JSON.stringify(response.error)}`
        );
      }

      return response.data as ExtractSourceResponse[];
    },
  });
};

/**
 * Delete a single mapped extract (note/snippet) by id.
 *
 * Backend: DELETE /report-generator/module-extracts/{extract_id} (204,
 * ownership-checked). On success, invalidate report-generator queries so the
 * source-snippets list (Notes panel) refreshes.
 */
export const useDeleteModuleExtract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (extractId: string): Promise<void> => {
      const response = await apiClient.delete({
        url: `/api/v1/report-generator/module-extracts/${extractId}`,
      });

      if (response.error) {
        throw new Error(
          `Failed to delete extract: ${JSON.stringify(response.error)}`
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR],
      });
    },
  });
};

/**
 * Persist the All-Notes drag order (MDRS-8).
 *
 * Backend: PATCH /report-generator/cases/{case_number}/module-extracts/order
 * with the full ordered list of extract ids; each extract's global_order is
 * set to its position. On success, invalidate report-generator queries so the
 * source-snippets list re-fetches with the new order.
 */
export const useReorderModuleExtracts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      caseNumber: string;
      templateId: string;
      orderedIds: string[];
    }): Promise<void> => {
      const response = await apiClient.patch({
        url: `/api/v1/report-generator/cases/${data.caseNumber}/module-extracts/order`,
        body: { template_id: data.templateId, ordered_ids: data.orderedIds },
        headers: { "Content-Type": "application/json" },
      });

      if (response.error) {
        throw new Error(
          `Failed to reorder notes: ${JSON.stringify(response.error)}`
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR],
      });
    },
  });
};

export type RetryAutoMapLoiResponse = {
  questions_written: number;
};

export const useRetryAutoMapLoi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (caseNumber: string): Promise<RetryAutoMapLoiResponse> => {
      const response = await apiClient.post({
        url: `/api/v1/report-generator/cases/${caseNumber}/auto-map-loi`,
      });

      if (response.error) {
        throw new Error(
          `Failed to re-run LOI auto-map: ${JSON.stringify(response.error)}`
        );
      }

      return response.data as RetryAutoMapLoiResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR],
      });
    },
  });
};
